<script lang="ts" setup>
import { PostFilterBy } from '@/blog/domain/PostFilterBy';
import BlogAside from '@/blog/infrastructure/components/BlogAside.vue';
import PostCard from '@/blog/infrastructure/components/PostCard.vue';
import PostCardSkeleton from '@/blog/infrastructure/components/PostCardSkeleton.vue';
import PostFilters from '@/blog/infrastructure/components/PostFilters.vue';
import PostPagination from '@/blog/infrastructure/components/PostPagination.vue';
import { usePostStore } from '@/blog/infrastructure/composition/usePostStore';
import LicenseContent from '@/page/infrastructure/components/LicenseContent.vue';
import ServicesSectionCe from '@/page/infrastructure/components/ServicesSection.ce.vue';
import { useHead, VisualFeedbackWrapperTypes, VisualFeedbackWrapper, useFilterStore, createNumberAdapter } from '@hfc/ui-core';
import { computed, onBeforeMount, ref, watch } from 'vue';

const { postStore, postState } = usePostStore();
const { filterStore } = useFilterStore();

const page = filterStore.register<number>({ key: 'page', fallbackValue: 1, mode: 'instant', adapter: createNumberAdapter() });
const perPage = 12;

const filterBy = filterStore.register<PostFilterBy>({ key: 'filterBy', fallbackValue: PostFilterBy.RECENT, mode: 'instant' });
const search = filterStore.register<string>({ key: 'search', fallbackValue: '', mode: 'instant' });
const category = filterStore.register<string>({ key: 'category', fallbackValue: '', mode: 'instant' });
const showError = ref(false);

const popular = computed(() => PostFilterBy.POPULARITY === filterBy.value);
const getPosts = async (): Promise<void> => {
  const categoryParam = category.value ? [category.value] : undefined;
  const response = await postStore.getPosts({ page: page.value, per_page: perPage, popular: popular.value, category: categoryParam, search: search.value || undefined });

  if (!response.success) {
    showError.value = true;

    return;
  }
  showError.value = false;
};

onBeforeMount(async () => {
  const categoryParam = category.value ? [category.value] : undefined;
  const blogPageResponse = await postStore.getBlogPage({ page: 1, per_page: perPage, popular: popular.value, category: categoryParam, search: search.value || undefined });
  if (!blogPageResponse.success) {
    showError.value = true;

    return;
  }
  showError.value = false;

  const vueHeadMeta = postState.value.blogPage.metaTags?.map(tag => {
    return {
      name: tag.name,
      property: tag.property,
      content: tag.content
    };
  }) ?? [];

  const hasDescription = vueHeadMeta.some(tag => tag.name === 'description');
  if (!hasDescription) {
    vueHeadMeta.push({ name: 'description', property: '', content: 'Find the latest California health care news and updates here. Learn about health care options, laws and regulations related to CA medical care.' });
  }

  const hasOgLocale = vueHeadMeta.some(tag => tag.property === 'og:locale');
  if (!hasOgLocale) {
    vueHeadMeta.push({ name: '', property: 'og:locale', content: 'en_US' });
  }

  const hasOgTitle = vueHeadMeta.some(tag => tag.property === 'og:title');
  if (!hasOgTitle) {
    vueHeadMeta.push({ name: '', property: 'og:title', content: postState.value.blogPage.headTitle ?? 'California Health Care News & Updates | Health for CA' });
  }

  const hasOgDescription = vueHeadMeta.some(tag => tag.property === 'og:description');
  if (!hasOgDescription) {
    vueHeadMeta.push({ name: '', property: 'og:description', content: 'Find the latest California health care news and updates here. Learn about health care options, laws and regulations related to CA medical care.' });
  }

  const hasOgSiteName = vueHeadMeta.some(tag => tag.property === 'og:site_name');
  if (!hasOgSiteName) {
    vueHeadMeta.push({ name: '', property: 'og:site_name', content: 'Health for California Insurance Center' });
  }

  useHead({
    meta: [...vueHeadMeta],
    link: [
      { rel: 'canonical', href: 'https://www.healthforcalifornia.com/blog' },
    ],
    title: postState.value.blogPage.headTitle ?? 'California Health Care News & Updates | Health for CA',
  });
  filterStore.commit();
});

watch([page, filterBy, category], async () => {
  await getPosts();
});

const totalPages = computed(() => postState.value.blogPage.totalPosts > perPage ? Math.ceil(postState.value.blogPage.totalPosts / perPage) : 1);

</script>
<template>
  <div class="w-full md:hidden bg-gray-100 py-2 text-center border-red-400">
    Lowest Prices. Simple Process.
  </div>
  <div class="bg-white md:pt-4 overflow-x-hidden">
    <h1 class="text-center bg-blue-600 text-white md:bg-white md:text-blue-600">Health Care Blog</h1>
    <main class="flex justify-center flex-col items-center mx-auto max-w-7xl gap-8 md:gap-12 p-2 md:p-6">
      <div v-if="showError" class="w-full max-w-sm sm:max-w-xl" >
        <VisualFeedbackWrapper show-box :severity="VisualFeedbackWrapperTypes.ERROR" title-class="text-center text-base font-semibold" title="Our blog is down">
          <div class="flex gap-4 items-center justify-center self-center text-sm">
            <hfc-icon icon="exclamation-circle" class="text-gray-50 bg-red rounded-full border border-gray-400 text-lg" />
            <p class="text-center text-gray-500 ">We're currently working to get it restored. Please try back later.</p>
          </div>
        </VisualFeedbackWrapper>
      </div>
      <div v-else class="flex flex-col md:flex-row gap-4 md:gap-10 lg:gap-14 ">
        <div class="flex flex-col gap-4 md:gap-8 items-center">
          <PostFilters v-model:category="category" v-model:filter-by="filterBy" />
          <ul class="grid grid-cols-1 lg:grid-cols-2  xl:grid-cols-3 gap-6">
            <template v-if="postState.allPostLoading">
              <PostCardSkeleton  v-for="i in 6" :key="i" />
            </template>
            <template v-else-if="!postState.allPosts.length">
              <div class="flex flex-col items-center justify-center col-span-full w-[17rem] lg:w-[34rem] xl:w-[51rem]  ">
                <h2 class="text-center text-2xl whitespace-nowrap">No results found</h2>
                <p class="text-center text-gray-500 whitespace-nowrap">Please enter another search term</p>
              </div>
            </template>
            <template v-else>
              <PostCard v-for="post in postState.allPosts" :key="post.slug" :post="post" />
            </template>
          </ul>
          <footer class="mt-10 flex w-full justify-center">
            <PostPagination v-model="page" :total-pages="totalPages"></PostPagination>
          </footer>
        </div>
        <div class="relative flex flex-col w-full md:w-auto overflow-visible">
          <div class="absolute left-0 bg-blue-500 font-normal text-2xl md:text-xl md:flex text-white px-6 py-4 hidden items-center justify-center w-[50vw] md:justify-start md:rounded-l-full z-10">
            Insurance & Pricing
          </div>
          <div class="flex md:hidden bg-blue-500 text-white px-6 py-4 rounded-full text-xl font-normal text-center items-center justify-center ">
            Insurance & Pricing
          </div>
          <div class="mt-4 md:mt-20">
            <BlogAside v-model="search" @getPosts="getPosts" />
          </div>
        </div>
      </div>
    </main>
    <div class="w-full">
      <div class="flex flex-col max-w-7xl mx-auto px-3 py-4">
        <ServicesSectionCe />
        <LicenseContent />
      </div>
    </div>
  </div>
</template>
